import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styleConsts from 'globalStyle/styleConsts.module.scss';
import { useEntityType } from 'hooks';
import { INITIAL_FORM_ACCOUNT_NAME } from 'hooks/queryParams/useInitialFormAccountName';
import {
    CAMPAIGNS_ROUTE,
    DIMENSION_TO_CREATIVE_ENTITY_MAP,
    PATH_TO_ENTITY_MAP,
    resolveReportIdByCampaignDimension,
} from 'modules/campaigns/config';
import { createCampaign } from 'modules/campaigns/flows';
import { postCampaignCreationSortState } from 'modules/campaigns/flows/createCampaign';
import { useAddPaymentModal } from 'modules/campaigns/hooks';
import { LAST_CREATED_CAMPAIGN_ID } from 'modules/campaigns/hooks/useLastCreatedCampaignId';
import { CAMPAIGN_REPORT_ID } from 'modules/campaigns/modules/campaigns-reports/config/reports/campaign/campaignReportId';
import { REPORT_SORTING } from 'modules/campaigns/modules/campaigns-reports/hooks/useReportSorting';
import { REPORT_ID } from 'modules/campaigns/modules/campaigns-reports/hooks/useSelectedReportId';
import { invalidateSubCountryData } from 'modules/campaigns/modules/common-campaign-form/actions';
import { useReportReachToGTM } from 'modules/campaigns/modules/common-campaign-form/components/ReachEstimator/hooks/useReportReachToGTM';
import { generateAssignCreativesCreatePath, generateCreativeCreatorPath } from 'modules/campaigns/services/utils';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormState } from 'modules/taboola-common-frontend-modules/forms';
import {
    mergeQueryParams,
    mergeQueryParamsWithLocation,
} from 'modules/taboola-common-frontend-modules/query-params/queryParamUtils';

const useCampaignCreatorState = ({ dimension }) => {
    const dispatch = useDispatch();
    const entityType = useEntityType(PATH_TO_ENTITY_MAP);
    const formAccount = useAccount();
    const { openModalAndGetPath: openAddPaymentModalAndGetPath } = useAddPaymentModal();
    const { submit, step, setStep, prevStep, nextStep } = useFormState({ waitAccountFetched: false });
    const { reportReachToGTM } = useReportReachToGTM();

    const createCampaignWrapper = useCallback(
        async successUrl => {
            const saveCampaign = campaign => dispatch(createCampaign(formAccount, campaign, entityType));
            return await submit({
                saveFormData: saveCampaign,
                successUrl,
            });
        },
        [submit, dispatch, formAccount, entityType]
    );

    const submitWrapper = useCallback(async () => {
        const reportId = resolveReportIdByCampaignDimension(CAMPAIGN_REPORT_ID, dimension);
        const campaignReportPath = mergeQueryParamsWithLocation(
            { pathname: CAMPAIGNS_ROUTE },
            {
                [REPORT_ID]: reportId,
                [REPORT_SORTING]: postCampaignCreationSortState,
            }
        );

        const getSuccessUrl = async () => (await openAddPaymentModalAndGetPath()) || campaignReportPath;
        const campaign = await createCampaignWrapper(getSuccessUrl);
        reportReachToGTM(campaign.id);
    }, [reportReachToGTM, createCampaignWrapper, dimension, openAddPaymentModalAndGetPath]);

    const handleSubmitCampaignAndNavigateToCreative = useCallback(() => {
        const creativeCreatorPath = generateCreativeCreatorPath({
            entity: DIMENSION_TO_CREATIVE_ENTITY_MAP[dimension],
        });

        const pathWithCampaignId = ({ id }) =>
            `${creativeCreatorPath}${mergeQueryParams('', {
                [LAST_CREATED_CAMPAIGN_ID]: id,
                [INITIAL_FORM_ACCOUNT_NAME]: formAccount.accountName,
            })}`;

        createCampaignWrapper(pathWithCampaignId);
    }, [formAccount, createCampaignWrapper, dimension]);

    const handleSubmitCampaignAndNavigateToAssignCreatives = useCallback(() => {
        const assignCreativesPath = ({ id: campaignId }) =>
            generateAssignCreativesCreatePath({
                accountId: formAccount.accountName,
                campaignId,
            });
        createCampaignWrapper(assignCreativesPath);
    }, [createCampaignWrapper, formAccount.accountName]);

    //clearing resources
    useEffect(() => () => dispatch(invalidateSubCountryData()), [dispatch]);

    return {
        step,
        handleSetStep: setStep,
        prevStep,
        nextStep,
        submit: submitWrapper,
        handleSubmitCampaignAndNavigateToCreative,
        wizardTemplateColumns: `${styleConsts.leftPaneWidth}% ${100 - styleConsts.leftPaneWidth}%`,
        handleSubmitCampaignAndNavigateToAssignCreatives,
    };
};

export default useCampaignCreatorState;
